/**
 * CMS Page Query
 *
 * Server-side query for fetching pages by slug.
 */

import { query } from "@solidjs/router";
import type { CMSPage, CMSPageResponse } from "~/types/cms";
import { env } from "~/config/env";
import { isPreviewTokenValid } from "~/utils/previewToken.server";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS, findByTenant } from "../queryUtils";
import { buildPageBySlugUrl } from "../queryUrls";
import { evictIfFull, touchEntry } from "~/lib/cache/cappedMap";

/** 15s cache — pages may be updated more frequently via CMS */
const CACHE_TTL_MS = 15_000;
const MAX_CACHE_SIZE = 200;
const pageCache = new Map<string, { data: CMSPage | null; expiresAt: number }>();

/** Clear the server-side page Map cache. Called by webhook invalidation. */
export function clearPageCache(slug?: string, tenantSlug?: string) {
  if (slug && tenantSlug) {
    pageCache.delete(`${slug}:${tenantSlug}`);
  } else {
    pageCache.clear();
  }
}

/**
 * Server-side query for fetching a page by slug
 *
 * @param slug - The page slug (e.g., 'test-bio-hotels')
 * @param tenantSlug - The tenant slug for multi-tenancy filtering
 * @param previewToken - CMS-signed preview token; drafts are read only when it verifies server-side for the tenant
 * @returns Page data or null if not found
 */
export const getPageBySlugQuery = query(
  async (slug: string, tenantSlug: string, previewToken?: string) => {
    "use server";
    const draft = isPreviewTokenValid(previewToken, tenantSlug);

    const LOG_PREFIX = "[pageBySlug]";

    // Skip cache for draft/preview requests
    if (!draft) {
      const cacheKey = `${slug}:${tenantSlug}`;
      const cached = pageCache.get(cacheKey);
      if (cached && Date.now() < cached.expiresAt) {
        touchEntry(pageCache, cacheKey);
        return cached.data;
      }
    }

    // URL built via shared queryUrls module (also used by performance audit)
    // depth=1: minimal population - blocks fetch their own deep data via createAsync
    const url = `${env.cmsApiUrl}${buildPageBySlugUrl({ slug, tenantSlug, draft })}`;

    const data = await fetchFromCMS<CMSPageResponse>(url, {
      logPrefix: LOG_PREFIX,
      previewToken: draft ? previewToken : undefined,
    });

    if (!data?.docs || data.docs.length === 0) {
      logger.warn(`${LOG_PREFIX} No page found with slug: ${slug}`);
      return null;
    }

    // Tenant is already scoped server-side via where[tenant.slug]; this is
    // defense-in-depth in case the query is ever changed to drop the filter.
    const page = findByTenant(data.docs, tenantSlug);

    if (!page) {
      logger.warn(`${LOG_PREFIX} No page found for tenant: ${tenantSlug}`);
      return null;
    }

    logger.info(`${LOG_PREFIX} Found page: ${page.title}`);
    if (!draft) {
      const cacheKey = `${slug}:${tenantSlug}`;
      evictIfFull(pageCache, MAX_CACHE_SIZE);
      pageCache.set(cacheKey, { data: page as CMSPage, expiresAt: Date.now() + CACHE_TTL_MS });
    }
    return page as CMSPage;
  },
  "pageBySlug"
);
